/**
 * @engine/vfx — Node-function emitter.
 *
 * Every node with a `shader` implementation compiles to a real module-level
 * Slang function; `main()` only wires calls. Node bodies are emitted verbatim —
 * the only rewrite is binding callable gradient/curve ports to their baked
 * sampler functions. Locals can't collide, `return;` returns from the node,
 * and each node type is emitted exactly once per module (per unique
 * gradient/curve dataset when specialized).
 */

import type { VFXNodeDefinition, VFXNodeInstance, VFXPortDefinition, VFXDataType } from '../../types';
import { SLANG_TYPES } from './slangTypes';
import { collectIdentifiers, contentHash, replaceCall, sanitizeIdent, stripComments } from './scan';
import {
  bakeGradientSampler,
  bakeCurveSampler,
  normalizeGradient,
  normalizeCurve,
  type BakedSampler,
} from './Samplers';

// ============================================================================
// ENVIRONMENT BUILTINS
// ============================================================================

/**
 * Values a node body may read from its execution environment. Detected by
 * identifier scan and passed as leading function parameters. Stage generators
 * declare which of these they can provide.
 */
export interface EnvBuiltin {
  /** Identifier as written in node bodies. */
  readonly name: string;
  /** Slang parameter declaration (without the name). */
  readonly paramType: string;
  /** `inout` parameters. */
  readonly inout?: boolean;
}

export const ENV_BUILTINS: readonly EnvBuiltin[] = [
  { name: 'particle', paramType: 'Particle', inout: true },
  { name: 'deltaTime', paramType: 'float' },
  { name: 'simulationTime', paramType: 'float' },
  /** Particle slot index in the particle buffer. */
  { name: 'idx', paramType: 'uint' },
  /** Spawn-local index (0..spawnCount) — init stage only. */
  { name: 'localIdx', paramType: 'uint' },
  /** Number of particles spawned this dispatch — init stage only. */
  { name: 'spawnCount', paramType: 'uint' },
  /** Source GPU event — init stage of event-driven systems. */
  { name: 'sourceEvent', paramType: 'GPUEvent' },
  /** Emitter velocity (world units/sec, from uniforms.emitterVelX/Y). */
  { name: 'emitterVelocity', paramType: 'float3' },
];

// ============================================================================
// EMITTED FUNCTION MODEL
// ============================================================================

export interface EmittedNodeFunction {
  /** Cache key (node type + specialization hash). */
  readonly key: string;
  /** Slang function name. */
  readonly name: string;
  /** Full Slang function source. */
  readonly code: string;
  /** Node type id (definition id) this function implements. */
  readonly nodeType: string;
  /** Node instance ids that call this function (grows as instances reuse it). */
  readonly users: Set<string>;
  /** Environment builtins required, in ENV_BUILTINS order (leading params). */
  readonly builtins: readonly EnvBuiltin[];
  /** Data input ports passed as params, in definition order (textures included). */
  readonly dataParams: readonly VFXPortDefinition[];
  /** Data output ports. 1 → return value; N → trailing `out` params. */
  readonly outputs: readonly VFXPortDefinition[];
  readonly returnKind: 'void' | 'single' | 'outparams';
  /** Gradient/curve samplers baked for this specialization. */
  readonly samplers: readonly BakedSampler[];
}

// ============================================================================
// HELPERS
// ============================================================================

function isDataPort(p: VFXPortDefinition): boolean {
  return p.kind !== 'flow';
}

function isBakedPort(p: VFXPortDefinition): boolean {
  return p.type === 'gradient' || p.type === 'curve';
}

export function portSlangType(p: VFXPortDefinition): string {
  return SLANG_TYPES[p.type as VFXDataType] ?? 'float';
}

/** Strip the common leading indentation of a template-literal body. */
export function dedentBody(code: string): string {
  const lines = code.replace(/\r\n/g, '\n').split('\n');
  while (lines.length && lines[0].trim() === '') lines.shift();
  while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
  let minIndent = Infinity;
  for (const line of lines) {
    if (line.trim() === '') continue;
    const indent = line.match(/^\s*/)![0].length;
    minIndent = Math.min(minIndent, indent);
  }
  if (!Number.isFinite(minIndent) || minIndent === 0) return lines.join('\n');
  return lines.map(l => (l.trim() === '' ? '' : l.slice(minIndent))).join('\n');
}

function indent(code: string, spaces: number): string {
  const pad = ' '.repeat(spaces);
  return code
    .split('\n')
    .map(l => (l.trim() === '' ? '' : pad + l))
    .join('\n');
}

// ============================================================================
// EMITTER
// ============================================================================

/**
 * Per-module cache of emitted node functions. Instances of the same node type
 * (and same gradient/curve data) share one function.
 */
export class NodeFunctionCache {
  private readonly fns = new Map<string, EmittedNodeFunction>();

  /** All emitted functions, in first-use order. */
  all(): readonly EmittedNodeFunction[] {
    return [...this.fns.values()];
  }

  /**
   * Get (or emit) the function for a node instance.
   * Returns null for nodes without a shader implementation.
   */
  getOrEmit(def: VFXNodeDefinition, node: VFXNodeInstance): EmittedNodeFunction | null {
    if (!def.shader?.code || stripComments(def.shader.code).trim() === '') return null;

    const bakedPorts = def.inputs.filter(p => isDataPort(p) && isBakedPort(p));

    // Bake samplers for this instance's gradient/curve data
    const samplers: BakedSampler[] = [];
    const bindings: Array<{ portId: string; fnName: string }> = [];
    for (const port of bakedPorts) {
      const value = node.inputValues[port.id] ?? port.defaultValue;
      const baked = port.type === 'gradient'
        ? bakeGradientSampler(normalizeGradient(value))
        : bakeCurveSampler(normalizeCurve(value));
      samplers.push(baked);
      bindings.push({ portId: port.id, fnName: baked.name });
    }

    const specialization = bindings.length > 0
      ? contentHash(bindings.map(b => b.fnName).join(','))
      : '';
    const key = specialization ? `${def.id}#${specialization}` : def.id;

    const cached = this.fns.get(key);
    if (cached) {
      cached.users.add(node.id);
      return cached;
    }

    const emitted = this.emit(def, key, specialization, samplers, bindings);
    emitted.users.add(node.id);
    this.fns.set(key, emitted);
    return emitted;
  }

  private emit(
    def: VFXNodeDefinition,
    key: string,
    specialization: string,
    samplers: BakedSampler[],
    bindings: Array<{ portId: string; fnName: string }>
  ): EmittedNodeFunction {
    const baseName = `n_${sanitizeIdent(def.id)}`;
    const fnName = specialization ? `${baseName}_${specialization.slice(0, 8)}` : baseName;

    // Bind callable gradient/curve ports to their baked sampler functions.
    let body = dedentBody(def.shader!.code);
    for (const { portId, fnName: samplerName } of bindings) {
      body = replaceCall(body, portId, samplerName);
    }

    const dataParams = def.inputs.filter(p => isDataPort(p) && !isBakedPort(p));
    const outputs = def.outputs.filter(isDataPort);

    // Detect environment builtins. Port names and body-declared locals shadow
    // builtins by contract (e.g. `var idx = ...;` is the author's local, not a
    // request for the particle index).
    const portIds = new Set([...dataParams.map(p => p.id), ...outputs.map(p => p.id)]);
    const declaredLocals = new Set(
      [...stripComments(body).matchAll(/\b(?:let|var)\s+([A-Za-z_][A-Za-z0-9_]*)/g)].map(m => m[1])
    );
    const ids = collectIdentifiers(body);
    const builtins = ENV_BUILTINS.filter(
      b => ids.has(b.name) && !portIds.has(b.name) && !declaredLocals.has(b.name)
    );

    // Return shape
    const returnKind: EmittedNodeFunction['returnKind'] =
      outputs.length === 0 ? 'void' : outputs.length === 1 ? 'single' : 'outparams';
    const returnType = returnKind === 'single' ? portSlangType(outputs[0]) : 'void';

    // Signature: env builtins, data inputs, then out-params (multi-output)
    const params = [
      ...builtins.map(b => `${b.inout ? 'inout ' : ''}${b.paramType} ${b.name}`),
      ...dataParams.map(p => `${portSlangType(p)} ${p.id}`),
      ...(returnKind === 'outparams' ? outputs.map(o => `out ${portSlangType(o)} ${o.id}`) : []),
    ];

    const lines: string[] = [];
    lines.push(`// ${def.name} (${def.id})`);
    lines.push(`${returnType} ${fnName}(${params.join(', ')})`);
    lines.push('{');
    if (returnKind === 'single') {
      lines.push(`    ${portSlangType(outputs[0])} ${outputs[0].id} = ${zeroOf(outputs[0])};`);
    } else if (returnKind === 'outparams') {
      // `out` params must be fully assigned on every path — zero-init up front.
      for (const o of outputs) {
        lines.push(`    ${o.id} = ${zeroOf(o)};`);
      }
    }
    lines.push(indent(body, 4));
    if (returnKind === 'single') {
      lines.push(`    return ${outputs[0].id};`);
    }
    lines.push('}');

    return {
      key,
      name: fnName,
      code: lines.join('\n'),
      nodeType: def.id,
      users: new Set<string>(),
      builtins,
      dataParams,
      outputs,
      returnKind,
      samplers,
    };
  }
}

function zeroOf(p: VFXPortDefinition): string {
  const t = portSlangType(p);
  switch (t) {
    case 'bool': return 'false';
    case 'int': return '0';
    case 'uint': return '0u';
    case 'float': return '0.0';
    default: return `${t}(0.0)`;
  }
}
